/**
 * Shared health/metric semantics for analytics surfaces.
 *
 * Single source of truth for the red/amber/green tiering used by Fleet
 * Analytics (uptime/coverage bars) and Device Analytics (uptime KPI), plus the
 * per-metric chart colors. Replaces the `uptimeColor()` helper that was
 * duplicated verbatim in analytics.tsx and device-uptime.tsx, and the
 * divergent hardcoded greens/ambers/blues across both pages.
 */
import { colors } from './tokens';

export type HealthTier = 'healthy' | 'degraded' | 'critical';

export interface HealthTierInfo {
  tier: HealthTier;
  /** Token color for the tier. */
  color: string;
  /** Human-readable label — the non-color channel for a11y (WCAG 1.4.1). */
  label: string;
  /** Compact glyph distinguishable without color (fill differs per tier). */
  glyph: string;
}

/**
 * Maps a 0–100 percentage to a health tier.
 * Thresholds: ≥90 healthy · 70–90 degraded · <70 critical.
 */
export function healthTier(pct: number): HealthTierInfo {
  if (pct >= 90) return { tier: 'healthy', color: colors.success.main, label: 'Healthy', glyph: '●' };
  if (pct >= 70) return { tier: 'degraded', color: colors.warning.main, label: 'Degraded', glyph: '◐' };
  return { tier: 'critical', color: colors.error.main, label: 'Critical', glyph: '○' };
}

/** Tier color only — drop-in replacement for the old `uptimeColor()`. */
export function healthColor(pct: number): string {
  return healthTier(pct).color;
}

/** Threshold caption for legends placed near uptime/coverage charts. */
export const HEALTH_THRESHOLD_LEGEND = '≥90% healthy · 70–90% degraded · <70% critical';

/**
 * Canonical per-metric chart colors so CPU and Memory read identically on the
 * fleet page and the device page (memory was orange on fleet, blue on device).
 */
export const metricColors = {
  cpu: colors.primary.main, // #FF6720 orange
  memory: colors.info.main, // #2F6BFF blue
} as const;

/**
 * Lighter step of each metric colour, for a companion series plotted beside
 * the main one (the p95 line next to its average).
 *
 * A real lighter step, not the main colour at reduced alpha: a 1px line at 40%
 * alpha all but vanished against the chart surface, and worse against the dark
 * one, so the p95 tail — the whole reason that series is drawn — could not be
 * read.
 *
 * `memory` takes secondary.light rather than info.light because info.light
 * (#EAF0FF) is a background tint, near-white and far too pale for a stroke;
 * secondary.main and info.main are the same blue, so its light step is the
 * matching one.
 */
export const metricColorsLight = {
  cpu: colors.primary.light, // #FF8D66
  memory: colors.secondary.light, // #82A6FF
} as const;

/**
 * Colour for a chart that plots a SINGLE series (device counts, enrollments,
 * event counts, version counts). Such a chart takes its identity from its
 * panel title, not its hue — the series never shares a plot with another, so
 * no hue has to tell them apart, and every one of them uses the brand accent.
 *
 * Deliberately not `metricColors.cpu`, despite the identical value: that token
 * means "the CPU series" in the one chart where hue separates CPU from Memory.
 * An enrollment bar is not CPU, and naming it so makes the next reader believe
 * the two are linked.
 *
 * Deliberately not a status colour either. `healthTier()` above hands out
 * success/warning/error on this same page, so a green bar reads "good" and an
 * amber bar reads "needs attention" — meaning this page really did assign to
 * boot counts and new enrollments, which carry no health verdict at all.
 */
export const seriesColor = colors.primary.main;
